// «Made with Guest.Gallery» on the guest page (approval 316): where the line
// goes and where it links. Pure, so the prerendered shell, the client and the
// proxy's guest-page count agree. Failure modes: tests/made-with/failure-modes.md.

export const MADE_WITH_SPOTS = {
  galleryEnd: "gallery-end",
  guestFooter: "guest-footer",
  galleryClosed: "gallery-closed",
} as const;

export type MadeWithSpot = (typeof MADE_WITH_SPOTS)[keyof typeof MADE_WITH_SPOTS];

/** The A/B arm: under the gallery's end (A) or in the guest-page footer (B). */
export type MadeWithArm =
  | typeof MADE_WITH_SPOTS.galleryEnd
  | typeof MADE_WITH_SPOTS.guestFooter;

// FNV-1a over the id's digits, then murmur3's finaliser, so the arm's bit
// depends on every digit (a plain id % 2 would follow creation order).
function hashEventId(id: number): number {
  let hash = 0x811c9dc5;
  const text = String(id);
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x85ebca6b);
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0xc2b2ae35);
  hash ^= hash >>> 16;
  return hash >>> 0;
}

function toEventId(eventId: unknown): number | null {
  const id =
    typeof eventId === "number"
      ? eventId
      : typeof eventId === "string" && /^\d{1,15}$/.test(eventId)
        ? Number(eventId)
        : Number.NaN;
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/**
 * The event's arm, from its id alone: the same on every render, request and
 * instance. An unusable id gets the footer (arm B), which shows on every page.
 */
export function getMadeWithArm(eventId: unknown): MadeWithArm {
  const id = toEventId(eventId);
  if (id === null) return MADE_WITH_SPOTS.guestFooter;
  return hashEventId(id) % 2 === 0
    ? MADE_WITH_SPOTS.galleryEnd
    : MADE_WITH_SPOTS.guestFooter;
}

/** The guest's locale home, tagged as an organic referral from this spot. */
export function buildMadeWithHref(locale: string, spot: MadeWithSpot): string {
  const home = /^[a-z]{2}$/.test(locale) ? locale : "en";
  return `/${home}?utm_source=guestgallery&utm_medium=referral&utm_campaign=made-with&utm_content=${spot}`;
}
